import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../supabaseClient';
import { useParams } from 'react-router-dom';

export default function SpecialOffers({ onOffersLoaded, style = 'chips' }) {
    const { restaurantId } = useParams();
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function fetchOffers() {
            try {
                const { data, error } = await supabase
                    .from('offers')
                    .select('*')
                    .eq('restaurant_id', restaurantId)
                    .eq('is_active', true)
                    .order('created_at', { ascending: false });

                if (error) throw error;

                const now = new Date();
                const valid = (data || []).filter(o => {
                    if (o.end_date && new Date(o.end_date) < now) return false;
                    if (o.start_date && new Date(o.start_date) > now) return false;
                    return true;
                });
                setOffers(valid);
                if (onOffersLoaded) onOffersLoaded(valid);
            } catch (_) {
                /* fail-soft */
            } finally {
                setLoading(false);
            }
        }
        if (restaurantId) fetchOffers();
    }, [restaurantId, onOffersLoaded]);

    if (loading) {
        return (
            <div className="mb-4 flex gap-2 overflow-hidden">
                {[1, 2, 3].map(i => (
                    <div key={i} className={`flex-none ${style === 'cards' ? 'h-24 w-64' : 'h-10 w-36'} skeleton-shimmer rounded-${style === 'cards' ? '2xl' : 'full'}`} />
                ))}
            </div>
        );
    }

    if (offers.length === 0) return null;

    return (
        <div className="mb-4 w-full">
            <div className="flex overflow-x-auto gap-2 pb-1 no-scrollbar">
                {offers.map((offer) => {
                    let label = offer.display_name || '';
                    let savingsHint = '';
                    if (offer.type === 'percentage') {
                        label = `${offer.discount_value}% OFF`;
                        savingsHint = label;
                    } else if (offer.type === 'flat') {
                        label = `₹${offer.discount_value} OFF`;
                        savingsHint = `Save ₹${offer.discount_value}`;
                    } else if (offer.type === 'free_item') {
                        label = 'Free Item';
                        savingsHint = '🎁 Free';
                    }

                    const isAutoApply = offer.is_auto_apply;
                    const hasCode = offer.promo_code && !isAutoApply;

                    // CARDS STYLE
                    if (style === 'cards') {
                        return (
                            <motion.div
                                key={offer.id}
                                whileTap={{ scale: 0.98 }}
                                className="flex-none w-64 h-24 bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-2xl p-4 shadow-sm relative overflow-hidden flex flex-col justify-center"
                            >
                                <div className="absolute -right-4 -bottom-4 text-6xl opacity-20">
                                    {offer.type === 'free_item' ? '🎁' : '🏷️'}
                                </div>
                                <h3 className="font-black text-xl leading-none mb-1 z-10">{label}</h3>
                                <p className="text-white/80 text-xs font-semibold z-10">{offer.description || savingsHint}</p>
                                <div className="mt-auto z-10">
                                    {isAutoApply ? (
                                        <span className="bg-white/20 px-2 py-0.5 rounded text-[10px] font-bold">Auto-applied at checkout</span>
                                    ) : hasCode ? (
                                        <span className="bg-white/20 px-2 py-0.5 rounded text-[10px] font-bold">Code: {offer.promo_code}</span>
                                    ) : null}
                                </div>
                            </motion.div>
                        );
                    }

                    // CHIPS STYLE (default)
                    return (
                        <motion.div
                            key={offer.id}
                            whileTap={{ scale: 0.95 }}
                            className="flex-none flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-3 py-1.5 cursor-default select-none"
                        >
                            <span className="text-sm">
                                {offer.type === 'free_item' ? '🎁' : offer.type === 'percentage' ? '🏷️' : '💰'}
                            </span>
                            <div className="flex flex-col leading-none">
                                <span className="text-[11px] font-black text-green-800 whitespace-nowrap">
                                    {savingsHint || label}
                                </span>
                                {hasCode && (
                                    <span className="text-[9px] font-bold text-green-600 uppercase tracking-wider whitespace-nowrap">
                                        Code: {offer.promo_code}
                                    </span>
                                )}
                                {isAutoApply && (
                                    <span className="text-[9px] font-bold text-green-600 whitespace-nowrap">
                                        ✨ Auto-applied
                                    </span>
                                )}
                                {offer.min_order_value > 0 && (
                                    <span className="text-[9px] text-green-500 whitespace-nowrap">
                                        Min ₹{offer.min_order_value}
                                    </span>
                                )}
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );
}
